import {
  Mitarbeiter_Markierung,
  Mitarbeiter_RotationsplanVorlageStationTyp,
  Mitarbeiter_RotationsplanVorlagenQuery,
  Mitarbeiter_RotationsplanVorlagenQueryVariables,
} from '@/gql/graphql';
import { useSuspenseQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { ROTATIONSPLAN_VORLAGEN } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlagen/graphql/queries';
import { getColorsForMarkierung } from '@/domain/mitarbeiter/features/rotationsplan/utils/getColorForMarkierung';
import { Markierung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Markierung';
import {
  getGesamtdauer,
  type Dauer,
  type Gesamtdauer,
} from '@/domain/mitarbeiter/features/rotationsplan/utils/getGesamtdauer';

export type VorlagenArbeitsbereich = {
  id: string;
  name: string;
  color: string;
};

export type VorlagenAbschnitt = {
  id: string;
  name: string;
  position: number;
  arbeitsbereiche: VorlagenArbeitsbereich[];
};

export type VorlagenStation = Dauer & {
  id: string;
  // null für einen Zeitraum ohne Station
  arbeitsbereich: VorlagenArbeitsbereich | null;
};

export type RotationsplanVorlage = {
  id: string;
  titel: string;
  abschnitte: VorlagenAbschnitt[];
  // alle Stationen in Reihenfolge der Abschnitte
  stationen: VorlagenStation[];
  gesamtdauer: Gesamtdauer;
};

export type VorlagenBeruf = {
  id: string;
  name: string;
  kuerzel: string;
  color: string;
  vorlagen: RotationsplanVorlage[];
};

type VorlageQueryStation =
  Mitarbeiter_RotationsplanVorlagenQuery['mitarbeiter_rotationsplanVorlagen'][number]['abschnitte'][number]['stationen'][number];

function toMarkierung(markierung: Mitarbeiter_Markierung): Markierung {
  return markierung as unknown as Markierung;
}

function toVorlagenStation(station: VorlageQueryStation): VorlagenStation {
  // ein Zeitraum ohne Station hat keinen Arbeitsbereich
  const arbeitsbereich =
    station.typ ===
      Mitarbeiter_RotationsplanVorlageStationTyp.ZeitraumOhneStation ||
    !station.arbeitsbereich
      ? null
      : {
          id: station.arbeitsbereich.id,
          name: station.arbeitsbereich.name,
          color: getColorsForMarkierung(
            toMarkierung(station.arbeitsbereich.markierung),
          ).base,
        };

  return {
    id: station.id,
    dauer: station.dauer,
    dauerEinheit: station.dauerEinheit,
    arbeitsbereich,
  };
}

export function useRotationsplanVorlagen(): {
  vorlagenByBeruf: VorlagenBeruf[];
  error?: string;
} {
  const result = useSuspenseQuery<
    Mitarbeiter_RotationsplanVorlagenQuery,
    Mitarbeiter_RotationsplanVorlagenQueryVariables
  >(ROTATIONSPLAN_VORLAGEN);

  const vorlagenByBeruf = useMemo(() => {
    const groupedByBeruf = new Map<string, VorlagenBeruf>();

    for (const vorlage of result.data?.mitarbeiter_rotationsplanVorlagen ??
      []) {
      const berufId = vorlage.beruf.id;
      const existing = groupedByBeruf.get(berufId);

      const berufColor = getColorsForMarkierung(
        toMarkierung(vorlage.beruf.markierung),
      ).base;

      const stationen: VorlagenStation[] = [];

      const abschnitte = [...vorlage.abschnitte]
        .sort((a, b) => a.position - b.position)
        .map((abschnitt) => {
          const bereicheById = new Map<string, VorlagenArbeitsbereich>();

          for (const station of [...abschnitt.stationen]
            .sort((a, b) => a.position - b.position)
            .map(toVorlagenStation)) {
            stationen.push(station);

            const { arbeitsbereich } = station;
            if (arbeitsbereich && !bereicheById.has(arbeitsbereich.id)) {
              bereicheById.set(arbeitsbereich.id, arbeitsbereich);
            }
          }

          return {
            id: abschnitt.id,
            name: abschnitt.name,
            position: abschnitt.position,
            arbeitsbereiche: [...bereicheById.values()],
          };
        });

      const mappedVorlage: RotationsplanVorlage = {
        id: vorlage.id,
        titel: vorlage.titel,
        abschnitte,
        stationen,
        gesamtdauer: getGesamtdauer(stationen),
      };

      if (!existing) {
        groupedByBeruf.set(berufId, {
          id: berufId,
          name: vorlage.beruf.name,
          kuerzel: vorlage.beruf.abkuerzung ?? '',
          color: berufColor,
          vorlagen: [mappedVorlage],
        });

        continue;
      }

      existing.vorlagen.push(mappedVorlage);
    }

    return [...groupedByBeruf.values()];
  }, [result.data]);

  if (result.error) {
    Sentry.captureException(result.error);
    return { vorlagenByBeruf: [] };
  }

  return { vorlagenByBeruf };
}
